import React, { useState, useRef, useEffect } from "react";
import { Button, Form, message, Switch, Input, InputNumber } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import InputCard from "../../../../Components/Cards/InputCard";
import dayjs from "dayjs";
import {
  getDriverById,
  updateDriver,
  getDriverDocumentTypes,
  deleteDriverDocument,
} from "../../../../API/FleetManager/Drivers";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.entries(data)
    .filter(([, msgs]) => Array.isArray(msgs) && msgs.length)
    .map(([field, msgs]) => `${field}: ${msgs.join(" ")}`);

  return fieldErrors.length ? fieldErrors.join(" | ") : fallback;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const MAX_FILENAME_LENGTH = 100;

const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
  if (file.name.length <= maxLength) return file;

  const dotIndex = file.name.lastIndexOf(".");
  const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
  const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
  const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

  return new File([file], `${truncatedBase}${ext}`, { type: file.type });
};

const EditDriver = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [form] = Form.useForm();

  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const fetchedDriverRef = useRef(null);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const res = await getDriverDocumentTypes();
        const list = res?.data?.data || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDocumentTypeOptions(
            list.map((t) => ({
              value: t.document_name,
              label: t.document_name,
              allowedFormats: t.allowed_formats || [],
            })),
          );
        }
      } catch (err) {
        console.error("Failed to load document types:", err);
      }
    };

    fetchDocumentTypes();
  }, []);

  const applyDriverData = (data) => {
    form.setFieldsValue({
      driver_name: data.name,
      dob: data.dob ? dayjs(data.dob) : undefined,
      phone_number: data.phone_number,
      email: data.email,
      jobTitle: data.job_title,
      address: data.address,
      license_number: data.license_number,
      issuing_date: data.license_issuing_date
        ? dayjs(data.license_issuing_date, "DD-MMM-YYYY")
        : undefined,
      expiry_date: data.license_expiry_date
        ? dayjs(data.license_expiry_date, "DD-MMM-YYYY")
        : undefined,
    });

    setAvatar(resolveAvatarUrl(data.avatar));
    setAvatarFile(null);

    if (Array.isArray(data.documents)) {
      setDocuments(
        data.documents.map((doc) => ({
          key: String(doc.id),
          id: doc.id,
          name: doc.file ? doc.file.split("/").pop() : doc.category,
          category: doc.category,
          date: formatDate(doc.uploaded_at),
          expiryDate: formatDate(doc.expiry_date),
          fileUrl: resolveAvatarUrl(doc.file),
        })),
      );
    }
  };

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No driver selected. Please open this page from the Drivers list.",
      );
      return;
    }

    const fetchDriver = async () => {
      try {
        const res = await getDriverById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) !== 2 || !data) {
          message.error(res?.data?.message || "Failed to load driver.");
          return;
        }

        fetchedDriverRef.current = data;
        applyDriverData(data);
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load driver.",
        );
      }
    };

    fetchDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedId, form]);

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const shortened = shortenFileName(file);
      setAvatarFile(shortened);

      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(shortened);
    }
  };

  const handleSubmit = async () => {
    if (!passedId) {
      message.error("No driver selected.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please fill in all required fields.");
      return;
    }

    const formData = new FormData();
    formData.append("name", values.driver_name ?? "");
    formData.append("phone_number", values.phone_number ?? "");
    formData.append("email", values.email ?? "");
    formData.append("job_title", values.jobTitle ?? "");
    formData.append("address", values.address ?? "");
    formData.append("license_number", values.license_number ?? "");
    if (values.dob) {
      formData.append("dob", values.dob.format("YYYY-MM-DD"));
    }
    if (values.issuing_date) {
      formData.append(
        "license_issuing_date",
        values.issuing_date.format("YYYY-MM-DD"),
      );
    }
    if (values.expiry_date) {
      formData.append(
        "license_expiry_date",
        values.expiry_date.format("YYYY-MM-DD"),
      );
    }
    if (avatarFile) {
      formData.append("avatar", avatarFile);
    }
    if (values.mobile_password) {
      formData.append("mobile_password", values.mobile_password);
    }

    const newDocuments = documents.filter((doc) => doc.file);
    newDocuments.forEach((doc) => {
      formData.append(doc.category, doc.file);
    });
    if (newDocuments.length > 0) {
      const expiryDates = {};
      newDocuments.forEach((doc) => {
        expiryDates[doc.category] = doc.expiryDate || null;
      });
      formData.append("document_expiry_dates", JSON.stringify(expiryDates));
    }

    setSaving(true);
    try {
      const res = await updateDriver(passedId, formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(res?.data, "Driver updated successfully!"),
        );
        navigate(-1);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update driver."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update driver."),
      );
    } finally {
      setSaving(false);
    }
  };

  const personalInformation = [
    {
      name: "driver_name",
      label: "Driver Name",
      type: "text",
      placeholder: "Enter Driver Name",
      rules: [{ required: true }],
    },
    {
      name: "dob",
      label: "Date of Birth",
      type: "date",
      placeholder: "Enter Date of Birth",
    },
    {
      name: "phone_number",
      label: "Phone Number",
      type: "phone",
      placeholder: "Enter Phone Number",
      rules: [{ required: true }],
    },
    {
      name: "email",
      label: "Email Address",
      type: "text",
      placeholder: "Enter Email Address",
      rules: [{ type: "email" }],
    },
    {
      name: "jobTitle",
      label: "Job Title",
      type: "text",
      placeholder: "Enter Job Title",
    },
    {
      name: "address",
      label: "Address",
      type: "text",
      placeholder: "Enter Address",
    },
  ];

  const passwordInformation = [
    {
      name: "mobile_password",
      label: "Mobile App Password",
      type: "password",
      placeholder: "Leave blank to keep current password",
      rules: [
        {
          validator: (_, value) => {
            if (!value || value.length >= 6) return Promise.resolve();
            return Promise.reject(
              new Error("Password must be at least 6 characters"),
            );
          },
        },
      ],
    },
    {
      name: "confirm_password",
      label: "Confirm Password",
      type: "password",
      placeholder: "Re-enter Password",
      dependencies: ["mobile_password"],
      rules: [
        ({ getFieldValue }) => ({
          validator(_, value) {
            const password = getFieldValue("mobile_password");
            if (!password || password === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error("Passwords do not match"));
          },
        }),
      ],
    },
  ];
  const licenseFields = [
    {
      name: "license_number",
      label: "License Number",
      type: "text",
      placeholder: "Enter License Number",
      rules: [{ required: true }],
    },
    {
      name: "issuing_date",
      label: "Issuing Date",
      type: "date",
      placeholder: "Enter Issuing Date",
    },
    {
      name: "expiry_date",
      label: "Expiry Date",
      type: "date",
      placeholder: "Enter Expiry Date ",
      rules: [
        {
          validator: (_, value) =>
            !value || value.isAfter(new Date(), "day")
              ? Promise.resolve()
              : Promise.reject(new Error("Expiry date must be a future date")),
        },
      ],
    },
  ];

  const handleCancel = () => {
    if (fetchedDriverRef.current) {
      applyDriverData(fetchedDriverRef.current);
    }
  };

  return (
    <div className="edit-company vehicle-detail">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <div className="header-wrapper">
        <div className="title">Edit Driver</div>
      </div>

      <Form form={form} layout="vertical">
        <div className="row">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Change Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  "T"
                )}
              </div>
            </div>
            <div className="profile-body"></div>
          </div>
        </div>

        <InputCard title="Personal Information" fields={personalInformation} />
        <InputCard title="License Details" fields={licenseFields} />

        <GenericDocumentManagerTable
          initialDocuments={documents}
          setDocuments={setDocuments}
          documentTypes={documentTypeOptions}
          allowSelectAll={true}
          onDeleteDocument={async (doc) => {
            if (!doc?.id) return;

            const res = await deleteDriverDocument(doc.id);

            if (Math.floor(res?.status / 100) !== 2) {
              throw new Error(
                getErrorMessage(res?.data, "Failed to delete document."),
              );
            }
          }}
        />

        <InputCard
          title="Driver App Credentials"
          fields={passwordInformation}
        />

        <div className="btns-row">
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={saving}
            disabled={saving}
          >
            Update Driver
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditDriver;
